/**
 * Message Component
 * Displays a single message with entry links and confidence scores.
 * 
 * Requirements 2.4, 2.5, 5.1, 5.2, 5.3
 */

import { ChatMessage } from '@/services/api';
import { cn } from '@/lib/utils';
import { FileText, User, Bot } from 'lucide-react';

interface MessageProps {
  message: ChatMessage;
  onEntryClick: (path: string) => void;
  onQuickReply?: (message: string) => void;
  onCaptureAction?: (action: NonNullable<ChatMessage['captureAction']>) => void;
  disableQuickReplies?: boolean;
}

export function Message({
  message,
  onEntryClick,
  onQuickReply,
  onCaptureAction,
  disableQuickReplies
}: MessageProps) {
  const isUser = message.role === 'user';
  const displayPath = message.filedEntryPath?.replace(/\.md$/, '');

  return (
    <div
      className={cn(
        'flex gap-3',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'
        )}
      >
        {isUser ? (
          <User className="h-4 w-4" />
        ) : (
          <Bot className="h-4 w-4" />
        )}
      </div>

      {/* Message content */}
      <div
        className={cn(
          'flex flex-col max-w-[80%]',
          isUser ? 'items-end' : 'items-start'
        )}
      >
        <div
          className={cn(
            'rounded-lg px-4 py-2',
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted'
          )}
        >
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        </div>

        {/* Entry link and confidence */}
        {message.filedEntryPath && (
          <button
            onClick={() => onEntryClick(message.filedEntryPath!)}
            className={cn(
              'flex items-center gap-1 mt-1 text-xs hover:underline',
              isUser ? 'text-primary-foreground/70' : 'text-muted-foreground'
            )}
          >
            <FileText className="h-3 w-3" />
            <span>{displayPath}</span>
            {message.filedConfidence !== undefined && (
              <span className="opacity-70">
                ({Math.round(message.filedConfidence * 100)}%)
              </span>
            )}
          </button>
        )}

        {/* Timestamp */}
        <span
          className={cn(
            'text-xs mt-1',
            isUser ? 'text-primary-foreground/50' : 'text-muted-foreground/50'
          )}
        >
          {new Date(message.createdAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>

        {!!message.quickReplies?.length && message.role === 'assistant' && onQuickReply && (
          <div className="mt-2 flex flex-wrap gap-2">
            {message.quickReplies.map((reply) => (
              <button
                key={reply.id}
                type="button"
                disabled={disableQuickReplies}
                onClick={() => onQuickReply(reply.message)}
                className={cn(
                  'min-h-[44px] rounded-full border px-3 text-xs transition-colors',
                  'border-border bg-background hover:bg-muted',
                  disableQuickReplies ? 'opacity-60 cursor-not-allowed' : ''
                )}
              >
                {reply.label}
              </button>
            ))}
          </div>
        )}

        {message.captureAction && message.role === 'assistant' && onCaptureAction && (
          <div className="mt-2">
            <button
              type="button"
              disabled={disableQuickReplies}
              onClick={() => onCaptureAction(message.captureAction!)}
              className={cn(
                'min-h-[44px] rounded-full border px-3 text-xs transition-colors',
                'border-border bg-background hover:bg-muted',
                disableQuickReplies ? 'opacity-60 cursor-not-allowed' : ''
              )}
            >
              {message.captureAction.label}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
